import * as React from "react";
import { FunctionComponent, lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Spinner } from "@amzn/awsui-components-react";
import { LegalAiCompanionProps, SkillType } from "@amzn/legal-ai-companion-types";
import { loadComponent } from "@amzn/legal-client-utilities";
import { get } from "lodash";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import { useInstanceState } from "../instance/instance-store";
import {
  convertSampleQuestionsToLegalAIQuestions,
  findInstanceConfig,
  setupRemoteModule,
  createKnowledgeBaseSkillConfiguration,
} from "./chatbot.utils";
import {
  ErrorFallback,
  PathfinderHeader,
  PathfinderAlphaTestingDisclaimerMessage,
} from "./chatbot.components";
import {
  ALPHA_TESTING_CONFIG,
  STANDARD_CONFIG,
  GUIDANCE_MESSAGES,
  FEEDBACK_COLORS,
} from "./chatbot.constants";

const LegalAiComponent = lazy(() =>
  loadComponent("LegalGenAIUIAssets", "./ai-companion", undefined, true)
) as unknown as React.ComponentType<LegalAiCompanionProps>;

const LegalAiCompanionComponent: React.FC = () => {
  const context = React.useContext(AppContext);
  const stageConfig = context.config;
  const { instanceId } = useInstanceState();
  const [isCrossInstanceEnabled, setIsCrossInstanceEnabled] = React.useState(false);

  if (!stageConfig.legalAICompanion) {
    return null;
  }

  const { documentGroupId, instancesConfig, stage, url } = stageConfig.legalAICompanion;

  const instanceConfig = React.useMemo(() =>
    findInstanceConfig(instancesConfig, instanceId),
    [instancesConfig, instanceId]
  );

  const sampleQuestions = React.useMemo(() =>
    convertSampleQuestionsToLegalAIQuestions(instanceConfig?.sampleQuestions),
    [instanceConfig?.sampleQuestions]
  );

  const skillConfiguration = React.useMemo(() =>
    createKnowledgeBaseSkillConfiguration(sampleQuestions, instanceId, documentGroupId, isCrossInstanceEnabled),
    [sampleQuestions, instanceId, documentGroupId, isCrossInstanceEnabled]
  );

  React.useEffect(() => {
    setupRemoteModule(url);
  }, [url]);

  const isAlphaTestingInstance = get(instanceConfig, "isAlphaTesting", false) === true;

  const legalAiCompanionProps: LegalAiCompanionProps = {
    stage: stage as any,
    currentPageId: "home",
    renderWithPortal: "#ai-companion",
    configuration: {
      name: "Pathfinder",
      description: "",
      commonSkillsForPage: [SkillType.KnowledgeBase],
      defaultSkillType: SkillType.KnowledgeBase,
      skillConfigurations: skillConfiguration,
      welcomeMessagePosition: isAlphaTestingInstance
        ? ALPHA_TESTING_CONFIG.WELCOME_MESSAGE_POSITION
        : STANDARD_CONFIG.WELCOME_MESSAGE_POSITION,
      welcomeMessageHeader: isAlphaTestingInstance
        ? "How can I help?"
        : undefined,
      mandatoryFeedback: isAlphaTestingInstance
        ? ALPHA_TESTING_CONFIG.MANDATORY_FEEDBACK
        : STANDARD_CONFIG.MANDATORY_FEEDBACK,
      feedbackColors: isAlphaTestingInstance ? FEEDBACK_COLORS : undefined,
      disclaimerMessage: isAlphaTestingInstance
        ? React.createElement(PathfinderAlphaTestingDisclaimerMessage)
        : undefined,
      inputPlaceholderDefaultText: "Enter your question here",
    },
    HeaderComponent: () => (
      <PathfinderHeader
        isCrossInstanceEnabled={isCrossInstanceEnabled}
        onCrossInstanceToggle={setIsCrossInstanceEnabled}
        isAlphaTestingInstance={isAlphaTestingInstance}
      />
    ),
    guidanceMessage: isAlphaTestingInstance
      ? GUIDANCE_MESSAGES.ALPHA
      : GUIDANCE_MESSAGES.STANDARD,
    termsOfServiceMessageComponent: isAlphaTestingInstance
      ? React.createElement("div")
      : undefined,
  };

  return (
    <div className="pathfinder-chatbot">
      <ErrorBoundary fallback={<ErrorFallback />}>
        <Suspense fallback={<Spinner />}>
          <LegalAiComponent
            {...legalAiCompanionProps}
            currentPageId="home"
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

const AiCompanionContentPlaceholderComponent: FunctionComponent = () => {
  useEffect(() => {
    const openEvent = new CustomEvent("onOpenWindow");
    const closeEvent = new CustomEvent("onCloseWindow");
    window.dispatchEvent(openEvent);
    return () => {
      window.dispatchEvent(closeEvent);
    };
  }, []);

  return (
    <div
      id="ai-companion"
      style={{ height: "100%" }}
    />
  );
};

export { AiCompanionContentPlaceholderComponent, LegalAiCompanionComponent };
